import React from "react";
import get from "lodash/get";
import { connect } from "react-redux";
import moment from "moment-timezone";
import { Link } from "react-router-dom";
import {
  Alert,
  Icon,
  Layout,
  Table,
  Badge,
  Button,
  Modal,
  Switch,
  message,
  Tooltip,
  Breadcrumb,
  Input,
} from "antd";

import AdminHeader from "../../components/AdminHeader/AdminHeader";
import Footer from "../../components/Footer/Footer";
import SettingsUserForm from "../../components/SettingsUserForm/SettingsUserForm";
import SettingsUserTrainingForm from "../../components/SettingsUserTrainingForm/SettingsUserTrainingForm";
import classes from "./SettingsUsers.css";

import isAuthenticated from "../../utils/isAuthenticated";
import {
  getSettingsUsersRequest,
  deleteUserRequest,
  activateUserRequest,
  getSitesRequest,
} from "../../api/requests";
import { logout } from "../../reducers/user/actions";
import {
  getUserCreatePermission,
  getUserUpdatePermission,
  getUserDeletePermission,
  getUserTimezone,
  getUserShortTimezone,
} from "../../reducers/user/selectors";
import { withParams } from "../../utils/url";

const routes = require("../../routes.json");

const { Content } = Layout;
const { Search } = Input;

class SettingsUsers extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      usersList: [],
      errorMessage: "",
      responseMessage: "",
      userEdit: null,
      userTrainingEdit: null,
      currentPage: 1,
      total: 1,
      pageSize: 25,
      sites: [],
      additionalSiteAccess: null,
      sortedInfo: {},
      order: null,
      nameSearch: null,
    };
  }

  async componentDidMount() {
    this.getUsersList();

    try {
      const { data } = await getSitesRequest();
      this.setState({ sites: data });
    } catch (e) {
      if (get(e, "response.status") === 401) {
        this.props.logout();
        return;
      }
      console.log(e);
    }
  }

  getUsersList = async (
    page = this.state.currentPage,
    per_page = this.state.pageSize
  ) => {
    this.setState({
      errorMessage: null,
      isLoading: true,
      responseMessage: null,
    });

    try {
      const { data } = await getSettingsUsersRequest({
        nameSearch: this.state.nameSearch,
        page,
        per_page,
        order_direction: this.state.order,
        order_column: get(this.state, "sortedInfo.columnKey"),
      });
      this.setState({
        usersList: data.data,
        total: data.total_pages * per_page,
        currentPage: page,
      });
    } catch (e) {
      if (get(e, "response.status") === 401) {
        this.props.logout();
        return;
      }
      this.setState({
        subject: null,
        errorMessage: get(e, "response.data.error", "Error"),
      });
    }

    this.setState({ isLoading: false });
  };

  onSwitchUserActivation = (code, state) => async () => {
    try {
      if (state === "active") {
        await deleteUserRequest(code);
      } else {
        await activateUserRequest(code);
      }

      message.success("Saved");

      this.getUsersList();
    } catch (e) {
      if (get(e, "response.status") === 401) {
        this.props.logout();
        return;
      }
      message.error(get(e, "response.data.error", "Error"));
      console.log(e);
    }
  };

  onUserEdit = (user) => () => {
    this.setState({ userEdit: user });
  };

  onUserTrainingEdit = (user) => () => {
    this.setState({ userTrainingEdit: user });
  };

  onUserEditHide = () => {
    this.setState({ userEdit: null });
  };

  onUserTrainingEditHide = () => {
    this.setState({ userTrainingEdit: null });
  };

  onUserEditSubmit = (id, userCreated) => {
    this.setState({ userEdit: null }, this.getUsersList);

    console.log(id, userCreated);

    if (!id && userCreated) {
      this.onUserTrainingEdit(userCreated)();
    }
  };

  onUserTrainingEditSubmit = () => {
    this.setState({ userTrainingEdit: null }, this.getUsersList);
  };

  addUser = () => {
    this.setState({ userEdit: {} });
  };

  handleTableChange = (pagination, filters, sorter) => {
    this.setState(
      {
        isLoading: true,
        pageSize: pagination.pageSize,
        sortedInfo: sorter,
        order:
          get(sorter, "order") === "ascend"
            ? "asc"
            : get(sorter, "order") === "descend"
            ? "desc"
            : "asc",
      },
      () => this.getUsersList(pagination.current, pagination.pageSize)
    );
  };

  onSubjectSearch = async (value) => {
    this.setState(
      { nameSearch: value, filter: null, site: null, eventsSelected: {} },
      this.getUsersList
    );
  };

  onSubjectChange = (e) => {
    this.setState({ nameSearch: e.target.value });
  };

  clearFilters = () => {
    this.setState({ nameSearch: null }, this.getUsersList);
  };

  render() {
    const {
      usersList,
      errorMessage,
      responseMessage,
      isLoading,
      userEdit,
      userTrainingEdit,
      currentPage,
      total,
      pageSize,
      sites,
      additionalSiteAccess,
      sortedInfo,
      nameSearch,
    } = this.state;
    const { userCreatePermission } = this.props;

    const columns = [
      {
        dataIndex: "code",
        key: "code",
        title: "Code",
        render: (val, record) => (
          <span>
            {val} &nbsp;{" "}
            <Badge
              count={record.state}
              style={{
                backgroundColor: record.state === "active" ? "#52c41a" : "#777",
              }}
            />
          </span>
        ),
        sorter: true,
        sortOrder: get(sortedInfo, "columnKey") === "code" && sortedInfo.order,
      },
      {
        dataIndex: "user_id",
        key: "user_id",
        title: "Randomization Platform - User ID",
        sorter: true,
        sortOrder:
          get(sortedInfo, "columnKey") === "user_id" && sortedInfo.order,
      },
      {
        dataIndex: "username",
        key: "username",
        title: "Username",
        sorter: true,
        sortOrder:
          get(sortedInfo, "columnKey") === "username" && sortedInfo.order,
      },
      {
        dataIndex: "first_name",
        key: "first_name",
        title: "Name",
        render: (val, record) => (
          <span>
            {val} {record.last_name}
          </span>
        ),
        sorter: true,
        sortOrder:
          get(sortedInfo, "columnKey") === "first_name" && sortedInfo.order,
      },
      {
        dataIndex: "email",
        key: "email",
        title: "Email",
        sorter: true,
        sortOrder: get(sortedInfo, "columnKey") === "email" && sortedInfo.order,
      },
      {
        dataIndex: "last_logged_in",
        key: "last_logged_in",
        title: "Last Logged In",
        render: (val) => (
          <span>
            {val
              ? `${moment
                  .utc(val)
                  .tz(this.props.userTimezone || "UTC")
                  .format("YYYY-MM-DD HH:mm")} ${
                  this.props.userShortTimezone || "UTC"
                }`
              : "No data"}{" "}
          </span>
        ),
        sorter: true,
        sortOrder:
          get(sortedInfo, "columnKey") === "last_logged_in" && sortedInfo.order,
      },
      {
        dataIndex: "site_access_list",
        key: "site_access_list",
        title: "Site access",
        render: (val) => (
          <div>
            {val.slice(0, 3).map(
              (item) =>
                item.state === "active" && (
                  <p key={item.site.id}>
                    <b>{item.site.name}:</b> {item.user_role.name}
                  </p>
                )
            )}

            {val.length > 3 && (
              <Button
                type="primary"
                size="small"
                htmlType="button"
                onClick={() =>
                  this.setState({ additionalSiteAccess: val.slice(3) })
                }
              >
                Additional site access
              </Button>
            )}
          </div>
        ),
      },
      {
        dataIndex: "mode",
        key: "mode",
        title: "User Lock-out",
        render: (val) => (
          <div>
            {val === true && "Automatic"}
            {val === false && "Manual"}
            {val === null && "Nothing"}
          </div>
        ),
        sorter: true,
        sortOrder: get(sortedInfo, "columnKey") === "mode" && sortedInfo.order,
      },
      {
        dataIndex: "id",
        key: "id",
        title: "Actions",
        render: (id, user) => (
          <div className={classes.actions}>
            <Tooltip placement="topLeft" title="Edit User">
              <Icon
                type="edit"
                theme="filled"
                className={classes.actionIcon}
                onClick={this.onUserEdit(user)}
              />
            </Tooltip>
            <Tooltip placement="topLeft" title="Edit Training">
              <Icon
                type="lock"
                theme="filled"
                className={classes.actionIcon}
                onClick={this.onUserTrainingEdit(user)}
              />
            </Tooltip>
            <Tooltip placement="topLeft" title="User Role Management">
              <Link
                to={withParams(routes.admin.settingsUsersRoles, { userID: id })}
              >
                <Icon
                  type="control"
                  theme="filled"
                  className={classes.roleIcon}
                />
              </Link>
            </Tooltip>
            &nbsp;&nbsp;
            <div>
              <b></b>
              <br />
              <Tooltip placement="topLeft" title="Activate / Deactivate User">
                <Switch
                  disabled={
                    (user.state === "active" &&
                      !this.props.userDeletePermission) ||
                    (user.state === "inactive" &&
                      !this.props.userUpdatePermission)
                  }
                  className={classes.reactivateBtn}
                  checked={user.state === "active"}
                  onChange={this.onSwitchUserActivation(id, user.state)}
                />
              </Tooltip>
            </div>
          </div>
        ),
      },
    ];

    return (
      <div style={{ backgroundColor: "#FAFAFA" }}>
        <Layout className={classes.parentContainer}>
          <AdminHeader />
          <Content className={classes.content}>
            <h3 className="headline-default">User Management</h3>
            <Breadcrumb>
              <Breadcrumb.Item href="/admin/settings">
                Admin Settings
              </Breadcrumb.Item>
              <Breadcrumb.Item>User Management</Breadcrumb.Item>
            </Breadcrumb>
            <br />

            <div className={classes.searchWrapper}>
              <label className={classes.searchWrapperSearch}>
                <b className={classes.searchWrapperLabel}>SEARCH</b>
                <Search
                  placeholder="By subject ID"
                  enterButton="Search"
                  size="large"
                  onSearch={this.onSubjectSearch}
                  value={nameSearch}
                  onChange={this.onSubjectChange}
                  style={{ maxWidth: "500px" }}
                />
              </label>

              {nameSearch && (
                <span
                  className={classes.clearButton}
                  onClick={this.clearFilters}
                >
                  Clear
                </span>
              )}
            </div>

            {errorMessage && <Alert type="error" message={errorMessage} />}

            {responseMessage && (
              <Alert type="success" message={responseMessage} />
            )}

            {userCreatePermission && (
              <div>
                <Button type="primary" onClick={this.addUser}>
                  Add user
                </Button>
                <br />
                <br />
              </div>
            )}

            <Table
              columns={columns}
              dataSource={usersList}
              loading={isLoading}
              rowKey="id"
              onChange={this.handleTableChange}
              pagination={{
                onShowSizeChange: (current, size) => {
                  this.setState({ pageSize: size });
                },
                current: currentPage,
                total,
                pageSize,
                showSizeChanger: true,
                pageSizeOptions: ["10", "25", "50", "100"],
              }}
            />

            <Modal
              visible={!!userEdit}
              onCancel={this.onUserEditHide}
              footer={null}
            >
              {userEdit && (
                <SettingsUserForm
                  {...userEdit}
                  isNew={userEdit && !userEdit.id}
                  onSubmit={this.onUserEditSubmit}
                  onCancel={this.onUserEditHide}
                />
              )}
            </Modal>

            <Modal
              visible={!!userTrainingEdit}
              onCancel={this.onUserTrainingEditHide}
              footer={null}
            >
              {userTrainingEdit && (
                <SettingsUserTrainingForm
                  {...userTrainingEdit}
                  sites={sites}
                  isNew={userTrainingEdit && !userTrainingEdit.id}
                  onSubmit={this.onUserTrainingEditSubmit}
                  onCancel={this.onUserTrainingEditHide}
                  getUsersList={this.getUsersList}
                />
              )}
            </Modal>

            <Modal
              title="Additional site access"
              visible={!!additionalSiteAccess}
              onCancel={() => {
                this.setState({ additionalSiteAccess: null });
              }}
              footer={null}
            >
              {additionalSiteAccess &&
                additionalSiteAccess.map(
                  (item) =>
                    item.state === "active" && (
                      <p key={item.site.id}>
                        <b>{item.site.name}:</b> {item.user_role.name}
                      </p>
                    )
                )}
            </Modal>
          </Content>
          <Footer noAdminLink />
        </Layout>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  userTimezone: getUserTimezone(state),
  userShortTimezone: getUserShortTimezone(state),
  userCreatePermission: getUserCreatePermission(state),
  userDeletePermission: getUserDeletePermission(state),
  userUpdatePermission: getUserUpdatePermission(state),
});

const mapDispatchToProps = { logout };

export default isAuthenticated(
  connect(mapStateToProps, mapDispatchToProps)(SettingsUsers)
);



// WEBPACK FOOTER //
// ./src/containers/SettingsUsers/SettingsUsers.js